import { Injectable } from "@angular/core";
import { ProductManagementSearchRequest } from "../../components/pbm/product-management/search-product/product-management-search-request";
import { ProductManagementSearchResult } from "../../components/pbm/product-management/search-product/product-management-search-result";
import { RemoteGatewayFactory } from "../../gateways/remote-gateway-factory";
import { ProductSubtype } from "../../models/domains/pbm/Benefit/product-subtype";
import { ProductType } from "../../models/domains/pbm/Benefit/product-type";
import { TherapeuticClass } from "../../models/domains/pbm/Benefit/therapeutic-class";
import { Pricing } from "../../models/domains/pbm/product-management/pricing";
import { PricingInfoViewItem } from "../../models/domains/pbm/product-management/pricing-info-view-item.model";
import { PriceRegime } from "../../models/domains/pbm/product-management/price-regime";
import { ProductLabelInfo } from "../../models/domains/pbm/product-management/product-label-info";
import { ProductUseValidity } from "../../models/domains/pbm/product-management/product-use-validity";
import { TributaryCreditConcession } from "../../models/domains/pbm/product-management/tributary-credit-concession";
import { UseType } from "../../models/domains/pbm/product-management/use-type";
import { MdmBackendUrl, MdmProductsBackendUrl } from "../../models/domains/urls/mdm-back-end.url";
import { ProductPricingsParser } from "../parsers/pbm/product-management/product-pricings.parser";
import { PriceRegimesParser } from "../parsers/pbm/product-management/price-regimes.parser";
import { ProductSubTypesParser } from "../parsers/pbm/product-management/product-sub-types.parser";
import { ProductTypesParser } from "../parsers/pbm/product-management/product-types.parser";
import { ProductUseValiditiesParser } from "../parsers/pbm/product-management/product-use-validities.parser";
import { SearchProductsParser } from "../parsers/pbm/product-management/search-products.parser";
import { TherapeuticClassParser } from "../parsers/pbm/product-management/therapeutic-class.parser";
import { TributaryCreditConcessionsParser } from "../parsers/pbm/product-management/tributary-credit-concessions.parser";
import { UseTypesParser } from "../parsers/pbm/product-management/use-types.parser";
import { TemplateProductPricing } from "../../models/domains/pbm/product-management/template-product-pricing";
import { ProductPrincingTemplateParser } from "../parsers/pbm/product-management/product-pricing-template.parser";

@Injectable()
export class ProductManagementRepository {
  constructor(
    private remoteGatewayFactory: RemoteGatewayFactory,
    private searchProductsParser: SearchProductsParser,
    private productTypesParser: ProductTypesParser,
    private productSubTypesParser: ProductSubTypesParser,
    private therapeuticClassParser: TherapeuticClassParser,
    private productUseValiditiesParser: ProductUseValiditiesParser,
    private ProductPricingsParser: ProductPricingsParser,
    private priceRegimesParser: PriceRegimesParser,
    private useTypesParser: UseTypesParser,
    private tributaryCreditConcessionsParser: TributaryCreditConcessionsParser,
    private productPrincingTemplateParser: ProductPrincingTemplateParser,
  ) {}

  public async searchProducts(request: ProductManagementSearchRequest): Promise<ProductManagementSearchResult> {
    let ModifiedStartAt = "";
    let ModifiedEndAt = "";
    if (request.getStartAt() && request.getEndAt()) {
      ModifiedStartAt = request.getStartAt().substr(6, 4) + request.getStartAt().substr(3, 2) + request.getStartAt().substr(0, 2);
      ModifiedEndAt = request.getEndAt().substr(6, 4) + request.getEndAt().substr(3, 2) + request.getEndAt().substr(0, 2) + " 23:59:00";
    }

    const body = JSON.stringify({
      StartIndex: request.getStartIndex(),
      TotalPerPages: request.getTotalPerPages(),
      FilterText: request.getFilterText() || "",
      PendingProducts: request.getPendingProducts() || false,
      ModifiedStartAt: ModifiedStartAt,
      ModifiedEndAt: ModifiedEndAt,
    });
    const headers = { "Content-Type": "application/json" };
    try {
      const remoteGateway = this.remoteGatewayFactory.createDefaultRemoteGateway();
      const data: any = await remoteGateway.postWithHeaders(new MdmProductsBackendUrl("/products/fetch"), body, headers);
      const searchResult: ProductManagementSearchResult = this.searchProductsParser.parse(data);
      searchResult.setRequest(request);
      return Promise.resolve(searchResult);
    } catch (error) {
      return Promise.reject(error);
    }
  }

  public async productTypes(): Promise<ProductType[]> {
    const headers = { "Content-Type": "application/json" };
    try {
      const remoteGateway = this.remoteGatewayFactory.createDefaultRemoteGateway();
      const data: any = await remoteGateway.getWithHeaders(new MdmProductsBackendUrl("/products/types"), headers);
      return Promise.resolve(this.productTypesParser.parse(data));
    } catch (error) {
      return Promise.reject(error);
    }
  }

  public async productSubTypes(productType: ProductType): Promise<ProductSubtype[]> {
    const headers = { "Content-Type": "application/json" };
    try {
      const remoteGateway = this.remoteGatewayFactory.createDefaultRemoteGateway();
      const data: any = await remoteGateway.getWithHeaders(new MdmProductsBackendUrl(`/products/type/${productType.getKey()}/subtypes`), headers);
      return Promise.resolve(this.productSubTypesParser.parse(data));
    } catch (error) {
      return Promise.reject(error);
    }
  }

  public async productPricings(productKey: string): Promise<Array<Pricing>> {
    const headers = { "Content-Type": "application/json" };
    const body = JSON.stringify({});
    try {
      const remoteGateway = this.remoteGatewayFactory.createDefaultRemoteGateway();
      const data: any = await remoteGateway.postWithHeaders(new MdmProductsBackendUrl(`/products/${productKey}/pricings`), body, headers);
      return Promise.resolve(this.ProductPricingsParser.parse(data));
    } catch (error) {
      return Promise.reject(error);
    }
  }

  public async productPrincingTemplate(): Promise<TemplateProductPricing[]> {
    const headers = { "Content-Type": "application/json" };
    try {
      const remoteGateway = this.remoteGatewayFactory.createDefaultRemoteGateway();
      const data: any = await remoteGateway.getWithHeaders(new MdmProductsBackendUrl("/products/default-pricing-quota-configurations"), headers);
      return Promise.resolve(this.productPrincingTemplateParser.parse(data));
    } catch (error) {
      return Promise.reject(error);
    }
  }

  public async updateProductPricing(productKey: string, pricingInfoViewItems: PricingInfoViewItem[]): Promise<any> {
    const headers = { "Content-Type": "application/json" };
    const body = JSON.stringify(
      pricingInfoViewItems.map((item) => {
        return {
          MaxPrice: item.getMaxPrice(),
          Price: item.getPrice(),
          Quota: item.getPricingQuotaConfiguration().getQuota(),
          StateId: item.getPricingQuotaConfiguration().getState().getStateKey(),
          FreeCommercialArea: item.getPricingQuotaConfiguration().isFreeCommercialArea(),
          PricingKey: item.getPricingKey(),
        };
      }),
    );
    try {
      const remoteGateway = this.remoteGatewayFactory.createDefaultRemoteGateway();
      const data: any = await remoteGateway.putWithHeaders(new MdmProductsBackendUrl(`/products/${productKey}/pricing`), body, headers);
      return Promise.resolve(data);
    } catch (error) {
      return Promise.reject(error.error.Data.ErrorMessage);
    }
  }

  public async insertProductPrincing(productKey: string, pricingInfoViewItems: PricingInfoViewItem[]) {
    const headers = { "Content-Type": "application/json" };
    const body = JSON.stringify(
      pricingInfoViewItems.map((item) => {
        return {
          MaxPrice: item.getMaxPrice(),
          Price: item.getPrice(),
          Quota: item.getPricingQuotaConfiguration().getQuota(),
          StateId: item.getPricingQuotaConfiguration().getState().getStateKey(),
          FreeCommercialArea: item.getPricingQuotaConfiguration().isFreeCommercialArea(),
          PricingKey: item.getPricingKey(),
        };
      }),
    );
    try {
      const remoteGateway = this.remoteGatewayFactory.createDefaultRemoteGateway();
      const data: any = await remoteGateway.postWithHeaders(new MdmProductsBackendUrl(`/products/${productKey}/pricing`), body, headers);
      return Promise.resolve(data);
    } catch (error) {
      return Promise.reject(error.error.Data.ErrorMessage);
    }
  }

  public async deleteProductPricing(productKey: string, pricingKey: string): Promise<any> {
    try {
      const remoteGateway = this.remoteGatewayFactory.createDefaultRemoteGateway();
      const data: any = await remoteGateway.delete(new MdmProductsBackendUrl(`/products/${productKey}/pricing/${pricingKey}`));
      return Promise.resolve(data);
    } catch (error) {
      return Promise.reject(error.error.Data.ErrorMessage);
    }
  }

  public async therapeuticClassByKey(key: string): Promise<TherapeuticClass> {
    const headers = { "Content-Type": "application/json" };
    try {
      const remoteGateway = this.remoteGatewayFactory.createDefaultRemoteGateway();
      const data: any = await remoteGateway.getWithHeaders(new MdmProductsBackendUrl(`/products/therapeutic-class/${key}`), headers);
      return Promise.resolve(this.therapeuticClassParser.parse(data));
    } catch (error) {
      return Promise.reject(error);
    }
  }

  public async productUseValidities(): Promise<ProductUseValidity[]> {
    const headers = { "Content-Type": "application/json" };
    try {
      const remoteGateway = this.remoteGatewayFactory.createDefaultRemoteGateway();
      const data: any = await remoteGateway.getWithHeaders(new MdmProductsBackendUrl("/products/product-use-validities"), headers);
      return Promise.resolve(this.productUseValiditiesParser.parse(data));
    } catch (error) {
      return Promise.reject(error);
    }
  }

  public async priceRegimes(): Promise<PriceRegime[]> {
    const headers = { "Content-Type": "application/json" };
    try {
      const remoteGateway = this.remoteGatewayFactory.createDefaultRemoteGateway();
      const data: any = await remoteGateway.getWithHeaders(new MdmProductsBackendUrl("/products/price-regime-types"), headers);
      return Promise.resolve(this.priceRegimesParser.parse(data));
    } catch (error) {
      return Promise.reject(error);
    }
  }

  public async useTypes(): Promise<UseType[]> {
    const headers = { "Content-Type": "application/json" };
    try {
      const remoteGateway = this.remoteGatewayFactory.createDefaultRemoteGateway();
      const data: any = await remoteGateway.getWithHeaders(new MdmProductsBackendUrl("/products/medical-prescription-use-types"), headers);
      return Promise.resolve(this.useTypesParser.parse(data));
    } catch (error) {
      return Promise.reject(error);
    }
  }

  public async tributaryCreditConcessions(): Promise<TributaryCreditConcession[]> {
    const headers = { "Content-Type": "application/json" };
    try {
      const remoteGateway = this.remoteGatewayFactory.createDefaultRemoteGateway();
      const data: any = await remoteGateway.getWithHeaders(new MdmProductsBackendUrl("/products/tributary-credit-concession-types"), headers);
      return Promise.resolve(this.tributaryCreditConcessionsParser.parse(data));
    } catch (error) {
      return Promise.reject(error);
    }
  }

  public async updateProduct(product: ProductLabelInfo): Promise<void> {
    const body = JSON.stringify({
      Ean: { Value: product.getEan1() },
      Ean2: { Value: product.getEan2() },
      Ean3: { Value: product.getEan3() },
      Substance: product.getSubstance(),
      FiscalNumber: product.getFiscalNumber().getValue(),
      Laboratory: product.getLaboratory(),
      GGREMCode: product.getGGREMCode(),
      Registry: product.getRegistry(),
      ProductName: product.getProductName(),
      Presentation: product.getPresentation(),
      TherapeuticClassId: product.getTherapeuticClass().getTherapeuticClassKey(),
      ProductUseValidityKey: product.getProductUseValidity().getKey(),
      ProductTypeKey: product.getProductType().getKey(),
      ProductSubTypeKey: product.getProductSubType().getKey(),
      PriceRegime: product.getPriceRegime().getKey(),
      HospitalRestriction: product.getHospitalRestriction(),
      Cap: product.getCap(),
      Confaz: product.getConfaz(),
      Icms0Percent: product.getIcms0Percent(),
      AppealAnalysis: product.getAppealAnalysis(),
      TributaryCreditConcession: product.getTributaryCreditConcession().getKey(),
      Commercialization: product.getCommercialization(),
      Targe: product.getTarge(),
      UseType: product.getUseType().getKey(),
    });
    const headers = { "Content-Type": "application/json" };
    try {
      const remoteGateway = this.remoteGatewayFactory.createDefaultRemoteGateway();
      const data: any = await remoteGateway.putWithHeaders(new MdmProductsBackendUrl(`/products/${product.getProductKey()}`), body, headers);
      return Promise.resolve(data);
    } catch (error) {
      const errorMessage: string = error.error.Data.ErrorMessage;
      return Promise.reject(errorMessage);
    }
  }

  public async insertProduct(product: ProductLabelInfo): Promise<string> {
    const body = JSON.stringify({
      Ean: { Value: product.getEan1() },
      Ean2: product.getEan2() ? { Value: product.getEan2() } : null,
      Ean3: product.getEan3() ? { Value: product.getEan3() } : null,
      Substance: product.getSubstance(),
      FiscalNumber: product.getFiscalNumber().getValue(),
      Laboratory: product.getLaboratory(),
      GGREMCode: product.getGGREMCode(),
      Registry: product.getRegistry(),
      ProductName: product.getProductName(),
      Presentation: product.getPresentation(),
      TherapeuticClassId: product.getTherapeuticClass().getTherapeuticClassKey(),
      ProductUseValidityKey: product.getProductUseValidity().getKey(),
      ProductTypeKey: product.getProductType().getKey(),
      ProductSubTypeKey: product.getProductSubType().getKey(),
      PriceRegime: product.getPriceRegime().getKey(),
      HospitalRestriction: product.getHospitalRestriction(),
      Cap: product.getCap(),
      Confaz: product.getConfaz(),
      Icms0Percent: product.getIcms0Percent(),
      AppealAnalysis: product.getAppealAnalysis(),
      TributaryCreditConcession: product.getTributaryCreditConcession().getKey(),
      Commercialization: product.getCommercialization(),
      Targe: product.getTarge(),
      UseType: product.getUseType().getKey(),
    });
    const headers = { "Content-Type": "application/json" };
    try {
      const remoteGateway = this.remoteGatewayFactory.createDefaultRemoteGateway();
      const data: any = await remoteGateway.postWithHeaders(new MdmProductsBackendUrl("/products/add"), body, headers);
      return Promise.resolve(data.Value);
    } catch (error) {
      const errorMessage: string = error.error.Data.ErrorMessage;
      return Promise.reject(errorMessage);
    }
  }
}
